/**
 * Gemini2 product gallery video loader.
 *
 * Works with the deferred markup in snippets/media-gemini2.liquid:
 *   <video class="js-product-video" preload="none" data-deferred-video>
 *     <source data-src="..." type="video/mp4">
 *   </video>
 *
 * Policy: a gallery video only loads + plays when its slide is visible in the
 * viewport AND belongs to the selected variant. Off-screen videos pause; videos
 * whose slide becomes variant-hidden are fully unloaded so they never download.
 */
(function () {
  'use strict';

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isSaveDataOrSlow() {
    var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!c) return false;
    if (c.saveData) return true;
    var t = (c.effectiveType || '').toLowerCase();
    return t === '2g' || t === 'slow-2g';
  }

  // When false (reduced motion / save-data / slow), never auto-load; require a tap.
  var AUTO_OK = !prefersReducedMotion() && !isSaveDataOrSlow();

  function loadSources(video) {
    if (!video || video.dataset.sourcesLoaded === 'true') return;
    var sources = video.querySelectorAll('source[data-src]');
    if (!sources.length) return;
    sources.forEach(function (s) {
      var url = s.getAttribute('data-src');
      if (url) {
        s.setAttribute('src', url);
        s.removeAttribute('data-src');
      }
    });
    video.load();
    video.dataset.sourcesLoaded = 'true';
  }

  function unloadSources(video) {
    if (!video || video.dataset.sourcesLoaded !== 'true') return;
    safePause(video);
    video.querySelectorAll('source[src]').forEach(function (s) {
      var url = s.getAttribute('src');
      if (url && !s.getAttribute('data-src')) s.setAttribute('data-src', url);
      s.removeAttribute('src');
    });
    video.removeAttribute('src');
    delete video.dataset.sourcesLoaded;
    video.load();
  }

  function isVariantHidden(video) {
    var slide = video.closest('.product__slide');
    return !!(slide && slide.classList.contains('variant-media--hidden'));
  }

  // Track the pending play() promise so we never pause() before it settles
  // (avoids "The play() request was interrupted by a call to pause()" AbortError).
  function safePlay(video) {
    loadSources(video);
    var p = video.play();
    if (p && typeof p.then === 'function') {
      video._playPromise = p;
      var clear = function () { if (video._playPromise === p) video._playPromise = null; };
      p.then(clear, clear);
    }
  }

  function safePause(video) {
    if (video._playPromise) {
      video._playPromise.then(function () { try { video.pause(); } catch (e) {} });
    } else {
      try { video.pause(); } catch (e) {}
    }
  }

  function playVideo(video) {
    if (isVariantHidden(video)) return;
    safePlay(video);
  }

  function Gallery(container) {
    this.container = container;
    this.videos = Array.prototype.slice.call(
      container.querySelectorAll('.js-product-video[data-deferred-video]')
    );
    if (this.videos.length) this.init();
  }

  Gallery.prototype.init = function () {
    var self = this;

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            if (AUTO_OK) playVideo(video);
          } else if (video.dataset.sourcesLoaded === 'true') {
            safePause(video);
          }
        });
      }, { threshold: [0, 0.5, 1], rootMargin: '0px' });

      this.videos.forEach(function (v) { self.observer.observe(v); });
    } else if (AUTO_OK) {
      this.videos.forEach(playVideo);
    }

    // Tap/click toggles playback (covers reduced-motion / save-data and acts as a fallback).
    this.videos.forEach(function (v) {
      v.addEventListener('click', function () {
        if (v.paused) playVideo(v); else safePause(v);
      });
    });

    // On variant change, unload videos that became hidden so they never download.
    this._onVariant = function () {
      self.videos.forEach(function (v) {
        if (isVariantHidden(v)) unloadSources(v);
      });
    };
    document.addEventListener('gemini2:variant-media-updated', this._onVariant);

    // Initial pass: drop any already-hidden variant videos.
    this._onVariant();
  };

  Gallery.prototype.destroy = function () {
    if (this.observer) this.observer.disconnect();
    if (this._onVariant) {
      document.removeEventListener('gemini2:variant-media-updated', this._onVariant);
    }
    this.videos.forEach(unloadSources);
  };

  var instances = [];

  function initAll() {
    document.querySelectorAll('[data-product-media-list]').forEach(function (c) {
      if (c.dataset.galleryVideoInit === 'true') return;
      c.dataset.galleryVideoInit = 'true';
      instances.push(new Gallery(c));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:section:unload', function () {
    instances.forEach(function (i) { if (i.destroy) i.destroy(); });
    instances = [];
    document.querySelectorAll('[data-product-media-list]').forEach(function (c) {
      c.dataset.galleryVideoInit = '';
    });
  });
})();
